export { ADULTING_PINNED_SECTIONS_STORAGE_KEY, getAdultingOverflowSections } from '../../core/sections';

export type AttentionLike = {
  id: string;
  text: string;
};

export type AttentionSurfaceGroupKey = 'overdue' | 'dueSoon' | 'notifications' | 'prompts';

export type AttentionSurfaceItem = AttentionLike & {
  label?: string | null;
  path?: string | null;
};

export type AttentionSurfaceTone = 'danger' | 'warning' | 'info' | 'neutral';

export type AttentionSurfaceBadge = {
  text: string;
  tone: AttentionSurfaceTone;
} | null;

export type AttentionSurfaceSection<T extends AttentionSurfaceItem> = {
  key: AttentionSurfaceGroupKey;
  title: string;
  subtitle: string;
  items: T[];
  totalCount?: number;
};

export type AttentionDismissalMap = Record<
  string,
  {
    signature: string;
    dismissedAt: string;
  }
>;

export function buildAttentionSignature(item: AttentionLike) {
  return `${item.id}::${item.text.trim()}`;
}

export function dismissAttentionItem<T extends AttentionLike>(
  item: T,
  existing: AttentionDismissalMap,
): AttentionDismissalMap {
  return {
    ...existing,
    [item.id]: {
      signature: buildAttentionSignature(item),
      dismissedAt: new Date().toISOString(),
    },
  };
}

export function filterDismissedAttentionItems<T extends AttentionLike>(
  items: T[],
  dismissals: AttentionDismissalMap,
) {
  return items.filter((item) => dismissals[item.id]?.signature !== buildAttentionSignature(item));
}

export function pruneAttentionDismissals<T extends AttentionLike>(
  items: T[],
  dismissals: AttentionDismissalMap,
) {
  const next: AttentionDismissalMap = {};
  const itemIds = new Set(items.map((item) => item.id));

  for (const [itemId, value] of Object.entries(dismissals)) {
    if (itemIds.has(itemId)) {
      next[itemId] = value;
    }
  }

  return next;
}

function getAttentionHaystack(item: AttentionSurfaceItem) {
  return `${item.label || ''} ${item.text || ''} ${item.path || ''}`.toLowerCase();
}

function getAttentionText(item: AttentionSurfaceItem) {
  return (item.text || '').toLowerCase();
}

function isNotificationItem(item: AttentionSurfaceItem) {
  const haystack = getAttentionHaystack(item);
  return (
    haystack.includes('message')
    || haystack.includes('notice')
    || haystack.includes('moderation')
    || haystack.includes('community')
  );
}

function isOverdueItem(item: AttentionSurfaceItem) {
  const text = getAttentionText(item);
  return text.includes('overdue') || text.includes('has passed');
}

function isDueSoonItem(item: AttentionSurfaceItem) {
  const text = getAttentionText(item);
  return (
    text.includes('due in ')
    || text.includes('due today')
    || text.includes('due tomorrow')
    || text.includes('coming up in ')
    || text.includes('renews in ')
    || text.includes('needs checking in ')
    || text.includes('needs attention in ')
    || text.includes('for today')
    || text.includes('the next few days')
  );
}

export function classifyAttentionSurfaceItem(item: AttentionSurfaceItem): AttentionSurfaceGroupKey {
  if (isNotificationItem(item)) {
    return 'notifications';
  }

  if (isOverdueItem(item)) {
    return 'overdue';
  }

  if (isDueSoonItem(item)) {
    return 'dueSoon';
  }

  return 'prompts';
}

export function getAttentionSurfaceBadge(item: AttentionSurfaceItem): AttentionSurfaceBadge {
  if (isNotificationItem(item)) {
    return { text: 'New', tone: 'info' };
  }

  const text = getAttentionText(item);

  if (isOverdueItem(item)) {
    return { text: 'Overdue now', tone: 'danger' };
  }

  if (text.includes('due today')) {
    return { text: 'Today', tone: 'warning' };
  }

  if (text.includes('due tomorrow')) {
    return { text: 'Tomorrow', tone: 'warning' };
  }

  const dayMatch = text.match(/\bin (\d+) day/);
  if (dayMatch) {
    return {
      text: `${dayMatch[1]}d left`,
      tone: 'warning',
    };
  }

  return null;
}

export function groupAttentionSurfaceItems<T extends AttentionSurfaceItem>(items: T[]) {
  return items.reduce<Record<AttentionSurfaceGroupKey, T[]>>(
    (groups, item) => {
      groups[classifyAttentionSurfaceItem(item)].push(item);
      return groups;
    },
    {
      overdue: [],
      dueSoon: [],
      notifications: [],
      prompts: [],
    },
  );
}

export function buildAttentionSurfaceSections<T extends AttentionSurfaceItem>(
  items: T[],
  options?: {
    expandedPrompts?: boolean;
    promptPreviewCount?: number;
  },
): AttentionSurfaceSection<T>[] {
  const groups = groupAttentionSurfaceItems(items);
  const promptPreviewCount = options?.promptPreviewCount ?? 3;
  const promptItems = options?.expandedPrompts ? groups.prompts : groups.prompts.slice(0, promptPreviewCount);
  const sections: AttentionSurfaceSection<T>[] = [
    {
      key: 'overdue',
      title: 'Overdue now',
      subtitle: 'The things that are already late, missing, or drifting.',
      items: groups.overdue,
    },
    {
      key: 'dueSoon',
      title: 'Due soon',
      subtitle: 'What is coming up next, while there is still time to deal with it calmly.',
      items: groups.dueSoon,
    },
    {
      key: 'notifications',
      title: 'Notifications',
      subtitle: 'Messages, notices, and anything new enough to clear with one quick look.',
      items: groups.notifications,
    },
    {
      key: 'prompts',
      title: 'Keep in view',
      subtitle: 'Useful nudges and setup gaps that matter, without burying the time-sensitive items.',
      items: promptItems,
      totalCount: groups.prompts.length,
    },
  ];

  return sections.filter((section) => section.items.length > 0 || section.key === 'prompts');
}
